/**
 * Pedir de antemano todo lo que una ficha va a necesitar al abrirse.
 *
 * Lo usan dos lugares, y por eso vive acá y no en uno de ellos:
 *
 * * `ViewSwitcher`, al abrir un registro: datos, botonera, seguimiento,
 *   adjuntos y las grillas visibles salen todos en la misma tanda, en vez de
 *   esperar a que el formulario se monte para pedir lo suyo.
 * * La lista, al dejar el mouse sobre una fila: medido con la demora real de
 *   producción, entre que la persona apunta y hace clic pasan unos cientos de
 *   milisegundos, y cada pedido cuesta ~0,3 s fijos más ~0,12 s por consulta.
 *   Pedir datos y botonera en ese rato hace que la ficha abra casi al instante.
 *
 * TODO SALE CON LA MISMA CLAVE Y LA MISMA FUNCIÓN que usa el componente que
 * después lo lee (`useRecordQuery` vía `claveDeFicha`, `useMetaDeFicha`,
 * `ChatterPanel`, `AttachmentsPanel`, `One2ManyField`). Si alguna se separara,
 * lo pedido de antemano no se usaría y se pagaría dos veces. Lo que ya está en
 * la caché y fresco no se vuelve a pedir: `prefetchQuery` respeta `staleTime`.
 *
 * Necesita la metadata DEL MODELO en la caché (la sin id), que es de donde
 * salen los campos a pedir. Sin ella no hace nada: es una ayuda, no un camino
 * obligatorio.
 */

import type { QueryClient } from '@tanstack/react-query';

import { parametrosInicialesDeLaGrilla } from '@/components/fields/One2ManyField';
import { QUERY_KEYS } from '@/components/shell/queries';
import { api } from '@/lib/api';
import type { ModelSpec } from '@/lib/types';
import { DATA_KEYS, camposDeFicha, claveDeFicha } from '@/views/queries';

export interface AlcanceDePrecarga {
  /**
   * `true`: también seguimiento, adjuntos y grillas (al abrir la ficha).
   * `false`: sólo datos y botonera, lo que la ficha necesita para dibujarse (al
   * pasar el mouse por una fila: barato, para no cargar el servidor con fichas
   * que quizás no se abren).
   */
  completo: boolean;
}

export function precargarFicha(
  cliente: QueryClient,
  companyId: number | null,
  modelo: string,
  id: number,
  { completo }: AlcanceDePrecarga,
): void {
  const base = cliente.getQueryData<ModelSpec>(QUERY_KEYS.meta(companyId, modelo, null));
  if (base === undefined) return;

  const campos = camposDeFicha(base);
  void cliente.prefetchQuery({
    queryKey: claveDeFicha(companyId, modelo, id, campos),
    queryFn: ({ signal }) => api.readRecord(modelo, id, campos, signal),
    staleTime: 15_000,
  });
  // La botonera evaluada contra el registro: misma clave que `useMetaDeFicha`.
  void cliente.prefetchQuery({
    queryKey: [...QUERY_KEYS.meta(companyId, modelo, id), 'acciones'],
    queryFn: ({ signal }) => api.fetchMetaAcciones(modelo, id, signal),
    staleTime: 2 * 60 * 1000,
  });
  if (!completo) return;

  if (base.chatter === true) {
    void cliente.prefetchQuery({
      queryKey: DATA_KEYS.chatter(companyId, modelo, id),
      queryFn: ({ signal }) => api.fetchChatter(modelo, id, signal),
      staleTime: 15_000,
    });
  }
  if (base.attachments === true) {
    void cliente.prefetchQuery({
      queryKey: ['adjuntos', companyId, modelo, id],
      queryFn: ({ signal }) => api.listAttachments(modelo, id, signal),
      staleTime: 15_000,
    });
  }
  // Las grillas que se ven al abrir —las de la cabecera y las de la primera
  // solapa—, sólo las que dibuja la grilla de siempre: un campo con otro widget
  // pide otra cosa.
  const formulario = base.views?.form;
  const visibles = [...(formulario?.groups ?? []), ...(formulario?.tabs?.[0]?.groups ?? [])];
  for (const nombre of visibles.flatMap((grupo) => grupo.fields)) {
    const campo = base.fields[nombre];
    if (campo === undefined || campo.type !== 'one2many') continue;
    if (campo.widget !== undefined && campo.widget !== null && campo.widget !== 'one2many') continue;
    const parametros = parametrosInicialesDeLaGrilla(campo, id);
    if (parametros === null || campo.relation === undefined) continue;
    const relacion = campo.relation;
    void cliente.prefetchQuery({
      queryKey: DATA_KEYS.list(companyId, relacion, parametros),
      queryFn: ({ signal }) => api.searchRead(relacion, parametros, signal),
      staleTime: 15_000,
    });
  }
}
